import {useCallback, useMemo, useRef, useSyncExternalStore} from 'react';

export const useMedia = <T>(queries: string[], values: T[], defaultValue: T) => {
  const queriesKey = JSON.stringify(queries);
  const valuesRef = useRef(values);
  const defaultValueRef = useRef(defaultValue);

  valuesRef.current = values;
  defaultValueRef.current = defaultValue;

  // Callers often construct the query array inline. Keying the memo by its
  // contents keeps the MediaQueryList instances and subscription stable.
  const mediaQueryLists = useMemo(
    () => queries.map((query) => window.matchMedia(query)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queriesKey]
  );

  const getSnapshot = useCallback(() => {
    const index = mediaQueryLists.findIndex((mql) => mql.matches);
    return index >= 0 ? valuesRef.current[index] : defaultValueRef.current;
  }, [mediaQueryLists]);

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      mediaQueryLists.forEach((mql) => {
        if (typeof mql.addEventListener === 'function') {
          mql.addEventListener('change', onStoreChange);
        } else {
          // eslint-disable-next-line deprecation/deprecation
          mql.addListener(onStoreChange);
        }
      });

      return () => {
        mediaQueryLists.forEach((mql) => {
          if (typeof mql.removeEventListener === 'function') {
            mql.removeEventListener('change', onStoreChange);
          } else {
            // eslint-disable-next-line deprecation/deprecation
            mql.removeListener(onStoreChange);
          }
        });
      };
    },
    [mediaQueryLists]
  );

  const getServerSnapshot = useCallback(() => defaultValueRef.current, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
};
